import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Trophy, ChartLineUp, ListBullets, Crown, MedalMilitary } from '@phosphor-icons/react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const thisMonth = () => new Date().toISOString().slice(0, 7);

const RANK_COLORS = ['#f59e0b', '#9ca3af', '#b45309']; // gold, silver, bronze

export default function ProductivityPage() {
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState({ rows: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [departments, setDepartments] = useState([]);
  const [departmentId, setDepartmentId] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { month };
      if (departmentId) params.department_id = departmentId;
      const [pr, dr] = await Promise.all([
        axios.get(`${API_URL}/api/productivity`, { params, withCredentials: true }),
        axios.get(`${API_URL}/api/departments`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setData(pr.data || { rows: [], summary: {} });
      setDepartments(dr.data || []);
    } catch (e) {
      toast.error('Failed to load: ' + (e.response?.data?.detail || e.message));
    }
    setLoading(false);
  }, [month, departmentId]);

  useEffect(() => { load(); }, [load]);

  const rows = data.rows || [];
  const top3 = rows.slice(0, 3);
  const chartData = useMemo(() => rows.slice(0, 15).map((r) => ({
    name: (r.name || '').slice(0, 14),
    Tasks: r.breakdown.tasks_score,
    OnTime: r.breakdown.ontime_score,
    Attendance: r.breakdown.attendance_score,
    Penalty: r.breakdown.penalty,
  })), [rows]);

  const scoreBadge = (s) => {
    if (s >= 80) return { l: 'EXCELLENT', bg: '#dcfce7', fg: '#15803d' };
    if (s >= 60) return { l: 'GREAT', bg: '#dbeafe', fg: '#1d4ed8' };
    if (s >= 40) return { l: 'AVERAGE', bg: '#fef3c7', fg: '#92400e' };
    return { l: 'NEEDS FOCUS', bg: '#fee2e2', fg: '#b91c1c' };
  };

  return (
    <div className="space-y-4" data-testid="productivity-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2" style={{ color: '#1a2e4a' }}>
            <Trophy size={22} weight="duotone" /> Productivity Dashboard
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Composite score = Tasks(40) + On-time Delivery(30) + Attendance(20) − Leaves/Permissions(up to 10)
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div>
            <Label className="text-[10px]">Month</Label>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40" data-testid="prod-month-input" />
          </div>
          <div>
            <Label className="text-[10px]">Department</Label>
            <select className="border rounded px-3 py-2 text-sm bg-white" value={departmentId}
              onChange={(e) => setDepartmentId(e.target.value)} data-testid="prod-dept-filter">
              <option value="">All departments</option>
              {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-3">
          <div className="text-[11px] uppercase tracking-wider text-gray-500">Employees</div>
          <div className="text-2xl font-bold mt-0.5" style={{ color: '#1a2e4a' }}>{data.summary.total_employees || 0}</div>
        </Card>
        <Card className="p-3">
          <div className="text-[11px] uppercase tracking-wider text-gray-500">Avg Score</div>
          <div className="text-2xl font-bold mt-0.5 text-blue-700">{data.summary.avg_score || 0}<span className="text-xs text-gray-400">/100</span></div>
        </Card>
        <Card className="p-3">
          <div className="text-[11px] uppercase tracking-wider text-gray-500">Tasks Done</div>
          <div className="text-2xl font-bold mt-0.5 text-emerald-700">{data.summary.tasks_completed_total || 0}</div>
        </Card>
        <Card className="p-3">
          <div className="text-[11px] uppercase tracking-wider text-gray-500">Permissions</div>
          <div className="text-2xl font-bold mt-0.5 text-amber-700">{data.summary.permissions_total || 0}</div>
        </Card>
      </div>

      {/* Top 3 podium */}
      {top3.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {top3.map((p, idx) => {
            const Icon = idx === 0 ? Crown : MedalMilitary;
            const color = RANK_COLORS[idx];
            return (
              <Card key={p.employee_id} className="p-4 relative overflow-hidden border-l-4" style={{ borderLeftColor: color }} data-testid={`top-${idx + 1}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Icon size={20} weight="fill" style={{ color }} />
                      <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color }}>
                        Rank #{p.rank}
                      </span>
                    </div>
                    <div className="text-base font-bold truncate" style={{ color: '#1a2e4a' }}>{p.name}</div>
                    <div className="text-[10px] text-gray-500 mt-0.5">{p.emp_id}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold" style={{ color }}>{p.total_score}</div>
                    <div className="text-[10px] text-gray-500">/100</div>
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-4 gap-1 text-[10px]">
                  <div className="text-center bg-blue-50 rounded p-1">
                    <div className="font-bold text-blue-700">{p.breakdown.tasks_score}</div>
                    <div className="text-gray-500">Tasks</div>
                  </div>
                  <div className="text-center bg-emerald-50 rounded p-1">
                    <div className="font-bold text-emerald-700">{p.breakdown.ontime_score}</div>
                    <div className="text-gray-500">On-time</div>
                  </div>
                  <div className="text-center bg-purple-50 rounded p-1">
                    <div className="font-bold text-purple-700">{p.breakdown.attendance_score}</div>
                    <div className="text-gray-500">Attend.</div>
                  </div>
                  <div className="text-center bg-rose-50 rounded p-1">
                    <div className="font-bold text-rose-700">-{p.breakdown.penalty}</div>
                    <div className="text-gray-500">Penalty</div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Chart: top 15 stacked bars */}
      {chartData.length > 0 && (
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <ChartLineUp size={16} /> <span className="text-sm font-bold uppercase tracking-wider" style={{ color: '#1a2e4a' }}>Score Breakdown · Top 15</span>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 50 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="name" angle={-35} textAnchor="end" height={70} tick={{ fontSize: 10 }} interval={0} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
              <Tooltip contentStyle={{ fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="Tasks" stackId="a" fill="#3b82f6" />
              <Bar dataKey="OnTime" stackId="a" fill="#10b981" />
              <Bar dataKey="Attendance" stackId="a" fill="#a855f7" />
              <Bar dataKey="Penalty" stackId="b" fill="#ef4444" />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* Full ranking table */}
      <Card className="p-0 overflow-hidden">
        <div className="px-4 py-2.5 flex items-center gap-2 text-sm font-bold uppercase tracking-wider" style={{ background: 'var(--dx-bg)', color: '#1a2e4a' }}>
          <ListBullets size={14} /> Full Ranking ({rows.length})
        </div>
        <table className="w-full text-sm">
          <thead style={{ background: 'var(--dx-bg)' }}>
            <tr>
              <th className="text-left px-3 py-2 text-xs font-semibold uppercase">#</th>
              <th className="text-left px-3 py-2 text-xs font-semibold uppercase">Employee</th>
              <th className="text-center px-3 py-2 text-xs font-semibold uppercase">Tasks</th>
              <th className="text-center px-3 py-2 text-xs font-semibold uppercase">On-Time</th>
              <th className="text-center px-3 py-2 text-xs font-semibold uppercase">Attend.</th>
              <th className="text-center px-3 py-2 text-xs font-semibold uppercase">Penalty</th>
              <th className="text-center px-3 py-2 text-xs font-semibold uppercase" style={{ background: '#e0f2fe' }}>Score</th>
              <th className="text-left px-3 py-2 text-xs font-semibold uppercase">Stats</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="py-8 text-center text-gray-500">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={8} className="py-8 text-center text-gray-500">No data for {month}.</td></tr>
            ) : rows.map((r) => {
              const b = scoreBadge(r.total_score);
              return (
                <tr key={r.employee_id} className="border-t hover:bg-gray-50" data-testid={`prod-row-${r.emp_id}`}>
                  <td className="px-3 py-2 font-mono text-[12px]">{r.rank}</td>
                  <td className="px-3 py-2">
                    <div className="font-medium">{r.name}</div>
                    <div className="text-[10px] text-gray-500">{r.emp_id}</div>
                  </td>
                  <td className="px-3 py-2 text-center text-[12px] text-blue-700">{r.breakdown.tasks_score}</td>
                  <td className="px-3 py-2 text-center text-[12px] text-emerald-700">{r.breakdown.ontime_score}</td>
                  <td className="px-3 py-2 text-center text-[12px] text-purple-700">{r.breakdown.attendance_score}</td>
                  <td className="px-3 py-2 text-center text-[12px] text-rose-700">{r.breakdown.penalty > 0 ? `-${r.breakdown.penalty}` : '—'}</td>
                  <td className="px-3 py-2 text-center font-bold" style={{ background: '#e0f2fe', color: '#0c4a6e' }}>
                    <div>{r.total_score}</div>
                    <span className="inline-block text-[8px] font-bold px-1.5 py-0.5 rounded mt-1" style={{ background: b.bg, color: b.fg }}>{b.l}</span>
                  </td>
                  <td className="px-3 py-2 text-[10px] text-gray-600">
                    {r.stats.completed_tasks}/{r.stats.total_tasks} tasks · {r.stats.present_days}/{r.stats.working_days} days · {r.stats.leave_days}L · {r.stats.permissions}P
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
